import React, { useState, useEffect } from 'react';
import { BellRing, Calendar, Clock, Trash2, AlertTriangle, Info, ChevronDown } from 'lucide-react';
import localforage from 'localforage';

const AlertManagementScreen = () => {
  // State management
  const [alerts, setAlerts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('regular');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    
  const isMobile = windowWidth <= 768;
  const [alertInput, setAlertInput] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    priority: 'info',
    scheduleItemId: null
  });

  // Fetch alerts and appointments on mount
  useEffect(() => {
    fetchAlerts();
    fetchAppointments();
    const alertInterval = setInterval(fetchAlerts, 30000);
    
    // Update window width on resize
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearInterval(alertInterval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calculate alert statistics
  const calculateStats = () => {
    const total = alerts.length;
    const active = alerts.filter(a => new Date(a.endTime) > new Date()).length;
    const important = alerts.filter(a => a.priority === 'important').length;
    return { total, active, important };
  };

  // API functions
  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const token = await localforage.getItem('token');
      
      // Use '/api/alerts/all' to get all alerts, including inactive ones
      const response = await fetch('https://lager-app-api.sunrise-avengers.ch/api/alerts/all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch alerts');
      
      const data = await response.json();
      console.log('Received alerts:', data.length);
      
      // Sort alerts: active first, then by priority, then by creation time (newest first)
      const now = new Date();
      const sortedAlerts = data.sort((a, b) => {
        // Check if alert is currently active
        const aActive = new Date(a.startTime) <= now && new Date(a.endTime) >= now;
        const bActive = new Date(b.startTime) <= now && new Date(b.endTime) >= now;
        
        // Active alerts come first
        if (aActive && !bActive) return -1;
        if (!aActive && bActive) return 1;
        
        // Then sort by priority
        if (a.priority === 'important' && b.priority !== 'important') return -1;
        if (a.priority !== 'important' && b.priority === 'important') return 1;
        
        // Then by creation time (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setAlerts(sortedAlerts);
    } catch (err) {
      setError('Error fetching alerts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  const createTestAlert = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await localforage.getItem('token');
      
      // Create a test alert that will be active for the next 5 minutes
      const response = await fetch('https://lager-app-api.sunrise-avengers.ch/api/alerts/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to create test alert');
      }
      
      const result = await response.json();
      console.log('Test alert created:', result);
      
      // Refresh the alerts list
      await fetchAlerts();
      
      alert('Test alert created successfully! It will be active for 5 minutes.');
    } catch (err) {
      setError('Error creating test alert: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  const formatDateForBackend = (dateTimeStr) => {
    // If the input already has a 'T', it's probably in ISO format already
    if (dateTimeStr.includes('T')) {
      return dateTimeStr;
    }
    
    // If it has a space between date and time, replace with 'T'
    if (dateTimeStr.includes(' ')) {
      return dateTimeStr.replace(' ', 'T');
    }
    
    // Otherwise, assume it's just a date and add time
    return `${dateTimeStr}T00:00:00`;
  };
  // Add this helpful debug button to your UI
  const DebugButton = () => (
    <button
      onClick={createTestAlert}
      style={{
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        backgroundColor: '#333',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
      }}
      title="Create Test Alert"
    >
      <span style={{ fontSize: '18px' }}>🔔</span>
    </button>
  );
  const fetchAppointments = async () => {
    try {
      const token = await localforage.getItem('token');
      const response = await fetch('https://lager-app-api.sunrise-avengers.ch/api/camps', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch appointments');
      const data = await response.json();
      const allAppointments = data.flatMap(camp => 
        (camp.schedule || []).map(item => ({
          ...item,
          campTitle: camp.title
        }))
      );
      setAppointments(allAppointments);
    } catch (err) {
      setError('Error fetching appointments: ' + err.message);
    }
  };

  const handleAppointmentSelect = (appointment) => {
    setSelectedAppointment(appointment);
    const startTime = appointment.date + 'T' + appointment.startTime;
    const endTime = appointment.date + 'T' + appointment.endTime;
    setAlertInput(prev => ({
      ...prev,
      startTime,
      endTime,
      scheduleItemId: appointment.id
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
  
    try {
      const token = await localforage.getItem('token');
      
      // Prepare alert data based on which tab is active
      let finalAlert;
      
      if (activeTab === 'scheduled' && selectedAppointment) {
        // For scheduled alerts, use the appointment's time window
        finalAlert = {
          ...alertInput,
          startTime: selectedAppointment.date + 'T' + selectedAppointment.startTime,
          endTime: selectedAppointment.date + 'T' + selectedAppointment.endTime,
          scheduleItemId: selectedAppointment.id
        };
      } else {
        // For regular alerts, use the user's input times
        finalAlert = { ...alertInput };
        
        // Ensure dates are properly formatted
        // If they don't include 'T', add it
        if (finalAlert.startTime && !finalAlert.startTime.includes('T')) {
          finalAlert.startTime = finalAlert.startTime.replace(' ', 'T');
        }
        if (finalAlert.endTime && !finalAlert.endTime.includes('T')) {
          finalAlert.endTime = finalAlert.endTime.replace(' ', 'T');
        }
      }
  
      console.log('Submitting alert:', finalAlert);
  
      const response = await fetch('https://lager-app-api.sunrise-avengers.ch/api/alerts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(finalAlert)
      });
  
      if (!response.ok) {
        let errorMessage = "Failed to create alert";
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If parsing json fails, use the default message
        }
        throw new Error(errorMessage);
      }
  
      // Get the created alert from response
      const createdAlert = await response.json();
      console.log('Alert created successfully:', createdAlert);
  
      // Reset form
      setAlertInput({
        title: '',
        description: '',
        startTime: '',
        endTime: '',
        priority: 'info',
        scheduleItemId: null
      });
      setSelectedAppointment(null);
      
      // Fetch alerts to update the list
      await fetchAlerts();
      
      // Success feedback
      alert('Alert created successfully!');
    } catch (err) {
      console.error('Error creating alert:', err);
      setError('Error creating alert: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  

  const deleteAlert = async (alertId) => {
    if (!window.confirm('Delete this alert?')) return;
    
    try {
      setLoading(true);
      const token = await localforage.getItem('token');
      const response = await fetch('https://lager-app-api.sunrise-avengers.ch/api/alerts/' + alertId, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete alert');
      await fetchAlerts();
      alert('Alert deleted successfully');
    } catch (err) {
      setError('Error deleting alert: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const styles = {
    container: {
      maxWidth: '2000px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      width: isMobile ? "110%" : "100%",
      marginTop: isMobile ? "0px" : 'auto',
      marginLeft: isMobile ? "-37px" : '-20px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '24px'
    },
    headerIcon: {
      width: '40px',
      height: '40px',
      color: '#da291c'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 12px 24px rgba(0, 0, 0, 0.05)',
      border: '1px solid rgba(0, 0, 0, 0.05)',
      animation: 'slideIn 0.5s ease-out'
    },
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      padding: '20px',
      backgroundColor: '#f8fafc',
      borderRadius: '12px',
      marginBottom: '24px',
      cursor: 'pointer',
      transition: 'transform 0.3s ease'
    },
    statBox: {
      backgroundColor: '#ffffff',
      padding: '20px',
      borderRadius: '12px',
      textAlign: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'transform 0.2s ease'
    },
    statNumber: {
      fontSize: '32px',
      fontWeight: '600',
      color: '#da291c',
      marginBottom: '8px'
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b'
    },
    tabs: {
      display: 'flex',
      gap: '4px',
      marginBottom: '24px',
      borderBottom: '1px solid #e2e8f0'
    },
    tab: {
      padding: '12px 24px',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: '2px solid transparent',
      cursor: 'pointer',
      fontSize: '15px',
      fontWeight: '500',
      color: '#64748b',
      transition: 'all 0.3s ease'
    },
    activeTab: {
      color: '#da291c',
      borderBottomColor: '#da291c'
    },
    form: {
      display: 'grid',
      gap: '20px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    input: {
      padding: '12px 16px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      outline: 'none'
    },
    textarea: {
      padding: '12px 16px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      minHeight: '120px',
      resize: 'vertical',
      transition: 'all 0.2s ease',
      outline: 'none'
    },
    select: {
      padding: '12px 16px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      backgroundColor: '#fff',
      cursor: 'pointer'
    },
    button: {
      padding: '12px 24px',
      backgroundColor: '#da291c',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    deleteButton: {
      backgroundColor: '#ef4444',
      padding: '8px 16px',
      fontSize: '13px'
    },
    appointmentList: {
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      maxHeight: '300px',
      overflowY: 'auto'
    },
    appointmentItem: {
      padding: '16px',
      borderBottom: '1px solid #e2e8f0',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    },
    selectedAppointment: {
      backgroundColor: '#f3f4f6'
    },
    alertCard: {
      padding: '24px',
      borderRadius: '12px',
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      marginBottom: '16px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.3s ease',
      animation: 'fadeIn 0.5s ease-out'
    },
    alertHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px'
    },
    alertTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0
    },
    alertMeta: {
      fontSize: '13px',
      color: '#64748b',
      display: 'flex',
      gap: '8px',
      alignItems: 'center'
    },
    alertDescription: {
      fontSize: '14px',
      color: '#374151',
      marginBottom: '16px',
      lineHeight: '1.5'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      backgroundColor: '#f3f4f6',
      borderRadius: '12px',
      fontSize: '12px',
      color: '#374151',
      gap: '4px'
    },
    timeInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '13px',
      color: '#64748b'
    },
    error: {
      padding: '12px 16px',
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      borderRadius: '8px',
      marginBottom: '16px',
      fontSize: '14px'
    },
    infoBox: {
      backgroundColor: '#f0f9ff',
      border: '1px solid #bae6fd',
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      color: '#0369a1',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px',
      animation: 'slideIn 0.3s ease-out'
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      fontSize: '16px',
      color: '#666',
    }
  };

  // Add keyframe animations
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @media (max-width: 768px) {
        .container {
          padding: 16px;
        }
        .card {
          padding: 16px;
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <BellRing size={32} style={styles.headerIcon} />
          <h1 style={styles.title}>Alert Management</h1>
        </div>

        <div style={styles.statsContainer} onClick={() => setShowStats(!showStats)}>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{calculateStats().total}</div>
            <div style={styles.statLabel}>Total Alerts</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{calculateStats().active}</div>
            <div style={styles.statLabel}>Active Alerts</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{calculateStats().important}</div>
            <div style={styles.statLabel}>Important Alerts</div>
          </div>
        </div>

        {/* Info Boxes for each tab */}
        {activeTab === 'regular' && (
          <div style={styles.infoBox}>
            <Info size={20} />
            <div>
              <strong>Regular Alert:</strong> Create a time-bound alert that will be displayed during 
              the specified time period. Perfect for general announcements or temporary notifications.
              <br/><br/>
              <strong>Example:</strong> "Lunch break extended by 30 minutes today from 12:00 to 14:00"
            </div>
          </div>
        )}

        {activeTab === 'scheduled' && (
          <div style={styles.infoBox}>
            <Info size={20} />
            <div>
              <strong>Scheduled Alert:</strong> Create an alert linked to a specific appointment. 
              The alert will automatically use the appointment's time period.
              <br/><br/>
              <strong>Example:</strong> "Remember to bring your laptop for the workshop"
            </div>
          </div>
        )}

        <div style={styles.tabs}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'regular' ? styles.activeTab : {})
            }}
            onClick={() => {
              setActiveTab('regular');
              setSelectedAppointment(null);
              setAlertInput(prev => ({
                ...prev,
                startTime: '',
                endTime: '',
                scheduleItemId: null
              }));
            }}
          >
            Regular Alert
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'scheduled' ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab('scheduled')}
          >
            Scheduled Alert
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          {activeTab === 'scheduled' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Select Appointment</label>
              <div style={styles.appointmentList}>
                {appointments.length === 0 ? (
                  <div style={{padding: '16px', textAlign: 'center', color: '#666'}}>
                    No appointments found
                  </div>
                ) : (
                  appointments.map(appointment => (
                    <div
                      key={appointment.id}
                      style={{
                        ...styles.appointmentItem,
                        ...(selectedAppointment?.id === appointment.id ? styles.selectedAppointment : {})
                      }}
                      onClick={() => handleAppointmentSelect(appointment)}
                    >
                      <div>
                        <div style={styles.alertTitle}>{appointment.title}</div>
                        <div style={styles.alertMeta}>{appointment.campTitle}</div>
                      </div>
                      <div>
                        <div style={styles.timeInfo}>
                          <Calendar size={14} />
                          {appointment.date}
                        </div>
                        <div style={styles.timeInfo}>
                          <Clock size={14} />
                          {appointment.startTime} - {appointment.endTime}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Title</label>
            <input
              type="text"
              value={alertInput.title}
              onChange={(e) => setAlertInput({ ...alertInput, title: e.target.value })}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={alertInput.description}
              onChange={(e) => setAlertInput({ ...alertInput, description: e.target.value })}
              style={styles.textarea}
              required
            />
          </div>

          {activeTab === 'regular' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Start Time</label>
                <input
                  type="datetime-local"
                  value={alertInput.startTime}
                  onChange={(e) => setAlertInput({ ...alertInput, startTime: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>End Time</label>
                <input
                  type="datetime-local"
                  value={alertInput.endTime}
                  onChange={(e) => setAlertInput({ ...alertInput, endTime: e.target.value })}
                  style={styles.input}
                  required
                />
              </div>
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Priority</label>
            <select
              value={alertInput.priority}
              onChange={(e) => setAlertInput({ ...alertInput, priority: e.target.value })}
              style={styles.select}
            >
              <option value="info">Info</option>
              <option value="important">Important</option>
            </select>
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: loading || (activeTab === 'scheduled' && !selectedAppointment) ? 0.5 : 1,
              cursor: loading || (activeTab === 'scheduled' && !selectedAppointment) ? 'not-allowed' : 'pointer'
            }}
            disabled={loading || (activeTab === 'scheduled' && !selectedAppointment)}
          >
            {loading ? 'Creating...' : 'Create Alert'}
          </button>
        </form>
      </div>

      <div style={styles.card}>
        <div style={styles.header}>
          <BellRing size={24} style={styles.headerIcon} />
          <h2 style={{...styles.title, fontSize: '24px'}}>Current Alerts</h2>
        </div>

        {loading && <div style={styles.loading}>Loading alerts...</div>}

        {!loading && alerts.length === 0 && (
          <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
            No alerts found. Create one above.
          </div>
        )}

        {alerts.map(alert => {
          const isExpired = new Date(alert.endTime) < new Date();
          const linkedAppointment = appointments.find(apt => apt.id === alert.scheduleItemId);

          return (
            <div
              key={alert.id}
              style={{
                ...styles.alertCard,
                opacity: isExpired ? 0.6 : 1,
                borderLeft: `4px solid ${alert.priority === 'important' ? '#ef4444' : '#0ea5e9'}`
              }}
            >
              <div style={styles.alertHeader}>
                <div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <h3 style={styles.alertTitle}>{alert.title}</h3>
                    {alert.priority === 'important' && (
                      <AlertTriangle size={16} color="#ef4444" />
                    )}
                  </div>
                  {linkedAppointment && (
                    <div style={styles.badge}>
                      <Calendar size={12} />
                      {linkedAppointment.title}
                    </div>
                  )}
                </div>
                <div>
                  <div style={styles.timeInfo}>
                    <Clock size={14} />
                    {new Date(alert.startTime).toLocaleString()}
                  </div>
                  <div style={styles.timeInfo}>
                    <Clock size={14} />
                    {new Date(alert.endTime).toLocaleString()}
                  </div>
                </div>
              </div>

              <p style={styles.alertDescription}>{alert.description}</p>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={styles.timeInfo}>
                  Created by: {alert.createdBy}
                  <br />
                  Created at: {new Date(alert.createdAt).toLocaleString()}
                </div>

                {!isExpired && (
                  <button
                    onClick={() => deleteAlert(alert.id)}
                    style={{...styles.button, ...styles.deleteButton}}
                  >
                    <Trash2 size={14} />
                    Delete Alert
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AlertManagementScreen;